import { MemberRole, getRoleDisplayName, getRoleColor, getRoleDescription } from "@/lib/permissions";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RoleBadgeProps {
  role: MemberRole;
  size?: "small" | "medium" | "large";
  showDescription?: boolean;
  abbreviated?: boolean;
}

export function RoleBadge({
  role,
  size = "medium",
  showDescription = false,
  abbreviated = false,
}: RoleBadgeProps) {
  const displayName = getRoleDisplayName(role);
  const description = getRoleDescription(role);
  const color = getRoleColor(role);

  const sizeClasses = {
    small: "text-xs px-2 py-0.5",
    medium: "text-sm px-2.5 py-0.5",
    large: "text-base px-3 py-1",
  };

  const abbreviatedNames: Record<MemberRole, string> = {
    treasury_secretary: "Secretary Treasurer",
    managing_member: "Managing",
    board_member_active: "Active Board",
    board_member_passive: "Passive Board",
    financial_specialist: "Financial",
    legal_specialist: "Legal",
    tax_specialist: "Tax",
    operations_manager: "Operations",
    advisor: "Advisor",
    observer: "Observer",
  };

  const badgeContent = (
    <Badge
      className={`${sizeClasses[size]} font-semibold border-2`}
      style={{
        backgroundColor: `${color}15`,
        borderColor: color,
        color: color,
      }}
    >
      {abbreviated ? abbreviatedNames[role] : displayName}
    </Badge>
  );

  if (showDescription) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badgeContent}</TooltipTrigger>
          <TooltipContent>
            <p className="font-semibold">{displayName}</p>
            <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badgeContent;
}
